/**
 * Minimal DOM construction helpers.
 *
 * Deliberately dependency-free and untyped beyond what the shell needs: this is not a
 * framework, it just removes the `createElement` / `setAttribute` noise from `UiShell.ts`.
 */

export type ElChild = Node | string | null | undefined | false;

export interface ElProps {
  class?: string | undefined;
  id?: string | undefined;
  text?: string | undefined;
  /** Written out as `data-testid`; the end-to-end suite selects on these. */
  testid?: string | undefined;
  attrs?: Readonly<Record<string, string | number>> | undefined;
}

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  props?: ElProps,
  ...children: ElChild[]
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (props) {
    if (props.class !== undefined) node.className = props.class;
    if (props.id !== undefined) node.id = props.id;
    if (props.testid !== undefined) node.setAttribute('data-testid', props.testid);
    if (props.text !== undefined) node.textContent = props.text;
    if (props.attrs) {
      for (const [name, value] of Object.entries(props.attrs)) {
        node.setAttribute(name, typeof value === 'number' ? String(value) : value);
      }
    }
  }
  for (const child of children) {
    if (child === null || child === undefined || child === false) continue;
    node.append(child);
  }
  return node;
}

const SVG_NS = 'http://www.w3.org/2000/svg';

/**
 * Single-path, stroke-only glyphs on a 24x24 grid so every icon shares one rendering style
 * and inherits `currentColor` from its button.
 */
const ICON_PATHS = {
  play: 'M8 5.5 18.5 12 8 18.5Z',
  stop: 'M7.5 7.5h9v9h-9z',
  sound: 'M5 9.5h3l4-3.5v12l-4-3.5H5zM16 9.6a3.4 3.4 0 0 1 0 4.8',
  mute: 'M5 9.5h3l4-3.5v12l-4-3.5H5zM16 9.5l4.5 5m0-5-4.5 5',
  camera: 'M20 12a8 8 0 0 1-13.7 5.6M4 12a8 8 0 0 1 13.7-5.6M4 7.2v4h4M20 16.8v-4h-4',
  move: 'M12 4v16M4 12h16M12 4 9.2 6.8M12 4l2.8 2.8M12 20l-2.8-2.8M12 20l2.8-2.8M4 12l2.8-2.8M4 12l2.8 2.8M20 12l-2.8-2.8M20 12l-2.8 2.8',
  target: 'M12 4v3m0 10v3M4 12h3m10 0h3M12 8.6a3.4 3.4 0 1 0 0 6.8 3.4 3.4 0 0 0 0-6.8z',
  swap: 'M6 8.5h12l-3.4-3.4M18 15.5H6l3.4 3.4',
  gear: 'M12 9.6a2.4 2.4 0 1 0 0 4.8 2.4 2.4 0 0 0 0-4.8zM12 3.6v2.2m0 12.4v2.2M5 7.6l1.9 1.1m10.2 6.6 1.9 1.1M5 16.4l1.9-1.1m10.2-6.6L19 7.6',
  gauge: 'M4 18a8 8 0 1 1 16 0M12 18l4.2-5.2',
  reset: 'M19.4 12a7.4 7.4 0 1 1-2.2-5.2M19.4 4.6V9h-4.4',
  cube: 'M12 3.6 20 8v8l-8 4.4L4 16V8zM4 8l8 4.4M20 8l-8 4.4M12 12.4V20.4',
  close: 'M6.5 6.5l11 11M17.5 6.5l-11 11',
  // Three dots, drawn as zero-length strokes so they inherit the shared round line cap.
  dots: 'M6 12h.01M12 12h.01M18 12h.01',
} as const;

export type IconName = keyof typeof ICON_PATHS;

export function svgIcon(name: IconName): SVGSVGElement {
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('focusable', 'false');
  svg.setAttribute('class', 'icon');
  const path = document.createElementNS(SVG_NS, 'path');
  path.setAttribute('d', ICON_PATHS[name]);
  svg.append(path);
  return svg;
}

/** One decimal place, e.g. `440.0`. Never emits a unit — callers own the suffix. */
export function formatHz(hz: number): string {
  return Number.isFinite(hz) ? hz.toFixed(1) : '0.0';
}

/** Milliseconds with adaptive precision so a readout never jitters in width. */
export function formatMs(ms: number): string {
  if (!Number.isFinite(ms)) return '0.0';
  return Math.abs(ms) >= 100 ? ms.toFixed(0) : ms.toFixed(1);
}

export function classNames(...parts: (string | false | null | undefined)[]): string {
  let out = '';
  for (const part of parts) {
    if (!part) continue;
    out = out === '' ? part : `${out} ${part}`;
  }
  return out;
}
